import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { addItems, money } from '@theme/holdra-cart';

/**
 * @typedef {{ id: number, price: number, available: boolean, options: string[] }} Variant
 * @typedef {{ options: string[], variants: Variant[] }} ProductData
 * @typedef {{
 *   initialVariantId: number,
 *   freeUnits: number,
 *   truserDiscount: number,
 *   bh: ProductData,
 *   truser: ProductData | null,
 *   extender: Variant | null
 * }} BundleData
 *
 * @typedef {object} BundleBuilderRefs
 * @property {HTMLScriptElement} data
 * @property {HTMLFieldSetElement} cards
 * @property {HTMLElement} mix
 * @property {HTMLElement} [sticky]
 * @property {HTMLElement} [stickyPrice]
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} submitText
 * @property {HTMLElement} message
 */

/**
 * Option names compared as keys, so "Size"/"Størrelse"/"Storlek" and "Color"/"Farge"/"Färg" match across products.
 * @param {string} name
 */
function optionKey(name) {
  const key = name.toLowerCase();
  if (['størrelse', 'storrelse', 'size', 'storlek'].includes(key)) return 'size';
  if (['farge', 'color', 'colour', 'färg'].includes(key)) return 'color';
  return key;
}

/**
 * Bundle builder on the product page: 1 bra, a 3-pack or a complete set, optional add-ons,
 * a live total on the button, and one /cart/add.js call for everything.
 * Bra 1 follows the product's variant picker; bra 2 and 3 default to "same as bra 1", and their colour
 * swatches follow bra 1's colour until the shopper picks one.
 *
 * @extends {Component<BundleBuilderRefs>}
 */
class BundleBuilderComponent extends Component {
  requiredRefs = ['data', 'cards', 'mix', 'submit', 'submitText', 'message'];

  /** @type {IntersectionObserver[]} */
  #observers = [];

  #cardsAbove = false;

  #footerVisible = false;

  /** @type {BundleData} */
  #data;

  /** @type {Variant | null} */
  #bh1 = null;

  /** @type {AbortController | undefined} */
  #abortController;

  #busy = false;

  connectedCallback() {
    super.connectedCallback();

    this.#data = JSON.parse(this.refs.data.textContent || '{}');
    this.#bh1 =
      this.#data.bh.variants.find((variant) => variant.id === this.#data.initialVariantId) ??
      this.#data.bh.variants[0] ??
      null;

    this.#abortController = new AbortController();
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#abortController.signal,
    });

    // ?bundle=three|complete preselects a card (used by the cart's "mix colours and sizes" link)
    const requested = new URLSearchParams(window.location.search).get('bundle');
    const card = requested && this.querySelector(`input[data-bundle][value="${CSS.escape(requested)}"]`);
    if (card instanceof HTMLInputElement) card.checked = true;

    this.#setupStickyBar();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
  }

  /** Sticky bar button: bring the offer cards back into view. */
  scrollToCards() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.refs.cards.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    const checked = this.refs.cards.querySelector('input[data-bundle]:checked');
    if (checked instanceof HTMLElement) checked.focus({ preventScroll: true });
  }

  /** Shows the mobile sticky bar while the offer cards are above the viewport and the footer isn't visible. */
  #setupStickyBar() {
    const { sticky, cards } = this.refs;
    if (!sticky) return;

    const cardsObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.#cardsAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.#updateSticky();
    });
    cardsObserver.observe(cards);
    this.#observers.push(cardsObserver);

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      const footerObserver = new IntersectionObserver(([entry]) => {
        this.#footerVisible = Boolean(entry?.isIntersecting);
        this.#updateSticky();
      });
      footerObserver.observe(footer);
      this.#observers.push(footerObserver);
    }
  }

  #updateSticky() {
    const { sticky } = this.refs;
    if (!sticky) return;
    const stuck = this.#cardsAbove && !this.#footerVisible;
    sticky.dataset.stuck = String(stuck);
    sticky.inert = !stuck;
  }

  /** @param {Event} [event] */
  handleChange(event) {
    // A colour swatch the shopper picked stops following bra 1
    const group = event?.target instanceof HTMLInputElement ? event.target.closest('[data-follow]') : null;
    if (group instanceof HTMLElement) group.dataset.follow = 'false';
    this.#render();
  }

  async addToCart() {
    if (this.#busy) return;

    const { items, problem } = this.#selection();
    if (problem || items.length === 0) {
      this.#showMessage(problem || this.dataset.unavailableLabel || '');
      return;
    }

    this.#busy = true;
    this.refs.submit.setAttribute('aria-busy', 'true');
    this.#showMessage('');

    const result = await addItems(this, items, { action: 'add', context: 'product', origin: 'bundle-builder' });

    this.#busy = false;
    this.refs.submit.removeAttribute('aria-busy');
    if (!result.ok) this.#showMessage(result.message || this.dataset.unavailableLabel || '');
  }

  /** @returns {string} */
  get #bundle() {
    const checked = this.querySelector('input[data-bundle]:checked');
    return checked instanceof HTMLInputElement ? checked.value : 'single';
  }

  /** @param {string} name */
  #addonChecked(name) {
    const input = this.querySelector(`[data-addon-input="${name}"]`);
    return input instanceof HTMLInputElement && input.checked && !input.closest('[hidden]');
  }

  /**
   * The value chosen for an option in one row: the checked swatch or the dropdown's value.
   * An empty string means "same as bra 1".
   * @param {Element | null} row
   * @param {string} name
   */
  #chosen(row, name) {
    // A mix-and-match pack row adds the pack with a fixed colour value (the real colours go on the order line)
    if (row instanceof HTMLElement && row.dataset.fixedOption === name) return row.dataset.fixedValue || '';
    const escaped = CSS.escape(name);
    const swatch = row?.querySelector(`input[type="radio"][data-option-name="${escaped}"]:checked`);
    if (swatch instanceof HTMLInputElement) return swatch.value;
    const select = row?.querySelector(`select[data-option-name="${escaped}"]`);
    return select instanceof HTMLSelectElement ? select.value : '';
  }

  /** Colour swatches the shopper hasn't touched show bra 1's colour. */
  #syncSwatches() {
    const bhOptions = this.#data.bh.options.map(optionKey);
    for (const group of this.querySelectorAll('.bundle-builder__swatches')) {
      if (!(group instanceof HTMLElement)) continue;
      if (group.dataset.follow === 'true') {
        const value = this.#bh1?.options[bhOptions.indexOf(optionKey(group.dataset.optionName || ''))];
        for (const input of group.querySelectorAll('input[type="radio"]')) {
          if (input instanceof HTMLInputElement) input.checked = input.value === value;
        }
      }
      const label = group.querySelector('[data-swatch-value]');
      const checked = group.querySelector('input[type="radio"]:checked');
      if (label) label.textContent = checked instanceof HTMLInputElement ? checked.value : '';
    }
  }

  /**
   * Finds the variant chosen in one row. An empty value means "same as bra 1".
   * @param {ProductData} product
   * @param {string} line
   * @returns {Variant | null}
   */
  #resolve(product, line) {
    const row = this.querySelector(`[data-line="${line}"]`);
    const bhOptions = this.#data.bh.options.map(optionKey);

    const wanted = product.options.map((name) => {
      const value = this.#chosen(row, name);
      if (value) return value;

      const bhIndex = bhOptions.indexOf(optionKey(name));
      if (bhIndex >= 0 && this.#bh1) return this.#bh1.options[bhIndex];
      return null;
    });

    return (
      product.variants.find((variant) =>
        variant.options.every((value, index) => wanted[index] === null || value === wanted[index])
      ) ?? null
    );
  }

  /**
   * Everything the current choices would add, merged per variant, plus the first problem found.
   * @returns {{ items: Array<{ id: number, quantity: number }>, problem: string, needsChoice: boolean, total: number }}
   */
  #selection() {
    const { truser, extender } = this.#data;
    const bundle = this.#bundle;
    const soldOut = this.dataset.soldOutLabel || '';
    const unavailable = this.dataset.unavailableLabel || '';

    /** @type {Map<number, number>} */
    const quantities = new Map();
    /** @type {Array<{ id: number, quantity: number, properties: Record<string, string> }>} */
    const packItems = [];
    let problem = '';
    let needsChoice = false;

    /** @param {Variant | null} variant @param {string} label @param {ProductData} [product] @param {string} [line] */
    const take = (variant, label, product, line) => {
      if (!variant) {
        const unmatched = product && line ? this.#unmatchedSameOption(product, line) : null;
        if (unmatched && !problem) needsChoice = true;
        problem ||= unmatched
          ? `${label}: ${this.dataset.chooseLabel || ''} ${unmatched.toLowerCase()}`.replace(/\s+/g, ' ')
          : `${label}: ${unavailable}`;
        return;
      }
      if (!variant.available) {
        problem ||= `${label}: ${soldOut}`;
        return;
      }
      // A pack with a colour per piece is its own line, with the colours on the order line
      const picker = line ? this.querySelector(`[data-line="${line}"] pack-colour-picker`) : null;
      if (picker && 'value' in picker && 'property' in picker && picker.value) {
        packItems.push({ id: variant.id, quantity: 1, properties: { [String(picker.property)]: String(picker.value) } });
        return;
      }
      quantities.set(variant.id, (quantities.get(variant.id) ?? 0) + 1);
    };

    take(this.#bh1, 'BH 1');
    if (bundle !== 'single') {
      take(this.#resolve(this.#data.bh, 'bh2'), 'BH 2', this.#data.bh, 'bh2');
      take(this.#resolve(this.#data.bh, 'bh3'), 'BH 3', this.#data.bh, 'bh3');
    }

    let truserVariant = null;
    if (truser && bundle === 'complete') {
      truserVariant = this.#resolve(truser, 'truser');
      take(truserVariant, this.#lineLabel('truser'), truser, 'truser');
    } else if (truser && this.#addonChecked('truser')) {
      truserVariant = this.#resolve(truser, 'truser-addon');
      take(truserVariant, this.#lineLabel('truser-addon'), truser, 'truser-addon');
    }

    if (extender && this.#addonChecked('extender')) {
      const title = this.querySelector('[data-addon="extender"] .bundle-builder__addon-title')?.textContent;
      take(extender, title?.split('–')[0]?.trim() || '');
    }

    const prices = this.#prices(truserVariant);
    let total = prices[bundle] ?? prices.single;
    if (bundle !== 'complete' && this.#addonChecked('truser')) total += prices.truserOffer;
    if (extender && this.#addonChecked('extender')) total += extender.price;

    /** @type {Array<{ id: number, quantity: number, properties?: Record<string, string> }>} */
    const items = [...quantities].map(([id, quantity]) => ({ id, quantity }));
    items.push(...packItems);
    return { items, problem, needsChoice, total };
  }

  /**
   * The option left on "same as bra 1" whose bra 1 value this product doesn't have (e.g. size XXL).
   * @param {ProductData} product
   * @param {string} line
   * @returns {string | null}
   */
  #unmatchedSameOption(product, line) {
    const row = this.querySelector(`[data-line="${line}"]`);
    const bhOptions = this.#data.bh.options.map(optionKey);
    for (const [index, name] of product.options.entries()) {
      const control = row?.querySelector(`[data-option-name="${CSS.escape(name)}"]`);
      if (!control || this.#chosen(row, name)) continue;
      const bhIndex = bhOptions.indexOf(optionKey(name));
      const bhValue = bhIndex >= 0 ? this.#bh1?.options[bhIndex] : undefined;
      if (bhValue && !product.variants.some((variant) => variant.options[index] === bhValue)) return name;
    }
    return null;
  }

  /** @param {string} line */
  #lineLabel(line) {
    return this.querySelector(`[data-line="${line}"] .bundle-builder__line-label`)?.textContent?.trim() || '';
  }

  /**
   * Card prices from the current variant prices, mirroring the automatic discounts.
   * @param {Variant | null} [truserVariant]
   */
  #prices(truserVariant = null) {
    const { freeUnits, truser, truserDiscount } = this.#data;
    const unit = this.#bh1?.price ?? 0;
    const three = unit * (3 - freeUnits);
    const truserPrice = (truserVariant ?? truser?.variants.find((v) => v.available) ?? truser?.variants[0])?.price ?? 0;
    const truserOffer = Math.max(0, truserPrice - truserDiscount);

    return {
      single: unit,
      three,
      complete: three + truserOffer,
      truserOffer,
      savings: { single: 0, three: unit * freeUnits, complete: unit * freeUnits + truserDiscount },
    };
  }

  #render() {
    const bundle = this.#bundle;
    const { truser } = this.#data;
    this.#syncSwatches();

    // Rows for bra 2/3 and the briefs, shown only for the bundles that include them
    for (const line of ['bh2', 'bh3']) this.#toggle(`[data-line="${line}"]`, bundle !== 'single');
    this.#toggle('[data-line="truser"]', bundle === 'complete');
    this.#toggle('[data-addon="truser"]', bundle !== 'complete');
    this.#toggle('[data-line="truser-addon"]', this.#addonChecked('truser'));

    const hasVisibleRow = [...this.refs.mix.querySelectorAll('[data-line]')].some(
      (row) => row instanceof HTMLElement && !row.hidden
    );
    this.refs.mix.hidden = !hasVisibleRow;

    // Card prices follow the selected bra 1 variant
    const { items, problem, needsChoice, total } = this.#selection();
    const prices = this.#prices();
    if (this.refs.stickyPrice) this.refs.stickyPrice.textContent = money(prices.single, this);
    const saveLabel = this.dataset.saveLabel || '';
    for (const card of ['single', 'three', 'complete']) {
      const price = this.querySelector(`[data-card-price="${card}"]`);
      if (price) price.textContent = money(prices[card], this);

      const save = this.querySelector(`[data-card-save="${card}"]`);
      if (save instanceof HTMLElement) {
        const saving = prices.savings[card];
        save.hidden = saving <= 0;
        save.textContent = saveLabel.replace('[amount]', money(saving, this));
      }
    }

    // Button: live total, or why it can't be added
    const canAdd = !problem && items.length > 0 && Boolean(truser || bundle !== 'complete');
    this.refs.submit.disabled = !canAdd;
    // A missing choice (e.g. briefs size) keeps the total on the button; the message says what to pick
    this.refs.submitText.textContent = canAdd || needsChoice
      ? `${this.dataset.buttonLabel} – ${money(total, this)}`
      : this.#bh1?.available === false
        ? this.dataset.soldOutLabel || ''
        : this.dataset.unavailableLabel || '';
    this.#showMessage(this.#bh1?.available === false ? '' : problem);
  }

  /** @param {string} selector @param {boolean} visible */
  #toggle(selector, visible) {
    const element = this.querySelector(selector);
    if (element instanceof HTMLElement) element.hidden = !visible;
  }

  /** @param {string} text */
  #showMessage(text) {
    this.refs.message.textContent = text;
    this.refs.message.hidden = !text;
  }

  /**
   * Keeps bra 1 in sync with the product's variant picker.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const resource = detail?.resource;
        this.#bh1 = resource?.id
          ? this.#data.bh.variants.find((variant) => variant.id === resource.id) ?? {
              id: resource.id,
              price: resource.price,
              available: resource.available,
              options: resource.options ?? [],
            }
          : null;
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bundle-builder] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}
